// reviewExport.ts — turn Review Mode comments into an actionable markdown document
// grouped by ACT → Chapter → content, in programme order, and get it off the
// device three ways (per the cross-platform parity rule):
//   • clipboard — navigator.clipboard (web + most webviews)
//   • file      — native: Filesystem write + Share sheet; web: Blob + <a download>
//   • share     — native share sheet of the same file
// Targets are included verbatim (e.g. `act1-c04-l06`) so each note maps straight
// back to the content manifest.
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import type { Programme } from '@/types';
import type { ReviewComment } from '@/services/progress';
import { labelPartsFor, type ReviewLabelParts } from '@/services/reviewLabels';

const FILENAME = 'renurture-review-notes.md';

interface Located {
  comment: ReviewComment;
  parts: ReviewLabelParts;
}

/** Build the grouped markdown. `exportedAt` is passed in (no Date.now here). */
export function buildReviewMarkdown(programme: Programme, comments: ReviewComment[], exportedAt: string): string {
  const head = `# ReNurture — review notes\n\n_Exported ${exportedAt} · ${comments.length} note${comments.length === 1 ? '' : 's'}_\n`;
  if (!comments.length) return `${head}\nNo comments yet.\n`;

  const located: Located[] = comments
    .map((comment) => ({ comment, parts: labelPartsFor(programme, comment) }))
    .sort(
      (a, b) =>
        a.parts.actOrder - b.parts.actOrder ||
        a.parts.chapterOrder - b.parts.chapterOrder ||
        a.parts.stepOrder - b.parts.stepOrder ||
        a.comment.createdAt.localeCompare(b.comment.createdAt),
    );

  const lines: string[] = [head];
  let curAct = '';
  let curChapter = '';
  for (const { comment, parts } of located) {
    if (parts.act !== curAct) {
      curAct = parts.act;
      curChapter = '';
      lines.push(`\n## ${curAct}`);
    }
    if (parts.chapter !== curChapter) {
      curChapter = parts.chapter;
      lines.push(`\n### ${curChapter}`);
    }
    const target =
      comment.kind === 'chapter'
        ? `**Chapter note** \`${comment.targetId}\``
        : `**${parts.step}** \`${comment.targetId}\``;
    const when = comment.updatedAt.slice(0, 10);
    // blockquote the comment text, preserving the author's line breaks
    const quoted = comment.text
      .split('\n')
      .map((l) => `> ${l}`)
      .join('\n');
    lines.push(`\n${target}\n${quoted}\n>\n> _${when}_`);
  }
  return `${lines.join('\n')}\n`;
}

/** Copy the markdown to the clipboard. Returns false if the clipboard is unavailable. */
export async function copyReviewMarkdown(markdown: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(markdown);
    return true;
  } catch {
    return false;
  }
}

/** Save/share the markdown as a file (native share sheet, or a web download). */
export async function exportReviewMarkdown(markdown: string): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    const { uri } = await Filesystem.writeFile({
      path: FILENAME,
      data: markdown,
      directory: Directory.Cache,
      encoding: Encoding.UTF8,
    });
    await Share.share({ title: 'ReNurture review notes', text: 'ReNurture review notes', url: uri });
  } else {
    const blob = new Blob([markdown], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = FILENAME;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}
